import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ConciergeLogo from "../../assets/images/conciergeLogoLight.svg";
import { api } from "../../api";
import { getEnvironmentConfig } from "../../utils/environment";
import BookingTimeSelector from "./BookingTimeSelector";
import Modal from "../common/Modal";
import moment from "moment";
import "./ManageBooking.css";

const ManageBooking = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const bookingIdFromQuery = searchParams.get("bookingid");

  // Get environment configuration
  const envConfig = getEnvironmentConfig();

  const [booking, setBooking] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [cartData, setCartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNewBookingFlow, setIsNewBookingFlow] = useState(false);

  // Modal states
  const [openBookingModal, setOpenBookingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedBookingTime, setSelectedBookingTime] = useState(null);
  const [checkModalOpen, setCheckModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Alert modal state
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    showCancel: false,
    onConfirm: null,
  });

  // Modal helper functions
  const showAlertModal = (
    title,
    message,
    type = "info",
    showCancel = false,
    onConfirm = null
  ) => {
    setAlertModal({
      isOpen: true,
      title,
      message,
      type,
      showCancel,
      onConfirm,
    });
  };

  const closeAlertModal = () => {
    setAlertModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Booking code input state
  const [bookingCodeInput, setBookingCodeInput] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Use bookingId from params or query, ensuring it's a string
  const currentBookingId = (() => {
    const id = bookingId || bookingIdFromQuery;
    if (!id) return null;
    // Ensure it's a string
    if (typeof id === "string") return id;
    if (typeof id === "number") return String(id);
    if (id && typeof id === "object") {
      return id.posBookingKey || id._id || id.id || String(id);
    }
    return String(id);
  })();

  useEffect(() => {
    if (currentBookingId) {
      // Ensure currentBookingId is converted to string
      const bookingIdString = String(currentBookingId);
      setBookingCodeInput(bookingIdString);
      fetchBookingData(bookingIdString, true);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBookingId]);

  const fetchBookingData = async (bookingKeyOverride, suppressAlerts = false) => {
    // Determine the booking key to use
    let bookingKey = bookingKeyOverride;
    
    // If no override provided, try to get from current booking or URL params
    if (!bookingKey) {
      // If we have a booking object, use its posBookingKey or _id
      if (booking?.posBookingKey) {
        bookingKey = booking.posBookingKey;
      } else if (booking?._id) {
        bookingKey = booking._id;
      } else {
        bookingKey = currentBookingId;
      }
    }
    
    if (!bookingKey) {
      setLoading(false);
      return false;
    }
    
    // Extract string value from bookingKey (handle objects, numbers, etc.)
    let bookingKeyString = "";
    if (typeof bookingKey === "string") {
      bookingKeyString = bookingKey.trim();
    } else if (typeof bookingKey === "number") {
      bookingKeyString = String(bookingKey);
    } else if (bookingKey && typeof bookingKey === "object") {
      // If it's an object, try to extract a string value
      bookingKeyString = bookingKey.posBookingKey || bookingKey._id || bookingKey.id || bookingKey.toString?.() || "";
      if (typeof bookingKeyString === "string") {
        bookingKeyString = bookingKeyString.trim();
      } else {
        bookingKeyString = String(bookingKeyString).trim();
      }
    } else {
      bookingKeyString = String(bookingKey || "").trim();
    }
    
    if (!bookingKeyString || bookingKeyString === "[object Object]") {
      setLoading(false);
      if (!suppressAlerts) {
        showAlertModal(
          "Invalid Booking Code",
          "Please enter a valid booking code.",
          "error"
        );
      }
      return false;
    }

    try {
      setLoading(true);
      
      // Close any existing error modals before starting a new search
      if (!suppressAlerts) {
        closeAlertModal();
      }

      let bookingResponse = null;
      let isNewFlow = false;

      try {
        const newBookingResponse = await api(
          `booking-flow/booking/${bookingKeyString}`,
          "GET"
        );
        console.log("New booking flow response:", newBookingResponse);
        if (
          newBookingResponse.status === 200 &&
          newBookingResponse.data &&
          newBookingResponse.data.booking
        ) {
          bookingResponse = newBookingResponse.data;
          isNewFlow = true;
          console.log("Found booking in new flow:", bookingResponse);
        }
      } catch (newBookingError) {
        console.log("New booking flow not found, trying old booking API:", newBookingError);
      }

      if (!bookingResponse) {
        try {
          const oldBookingResponse = await api(`booking/${bookingKeyString}`, "GET");
          console.log("Old booking flow response:", oldBookingResponse);
          if (
            oldBookingResponse.status === 200 &&
            oldBookingResponse.data &&
            oldBookingResponse.data.booking
          ) {
            bookingResponse = oldBookingResponse.data;
            isNewFlow = false;
            console.log("Found booking in old flow:", bookingResponse);
          }
        } catch (oldBookingError) {
          console.log("Old booking flow also not found:", oldBookingError);
        }
      }

      if (bookingResponse && bookingResponse.booking) {
        const normalizedBooking = normalizeBookingData(
          bookingResponse,
          isNewFlow
        );

        setBooking(normalizedBooking);
        setCartData(bookingResponse.cart);
        setLocationData(bookingResponse.location || normalizedBooking.location);
        setIsNewBookingFlow(isNewFlow);

        if (normalizedBooking.date && normalizedBooking.time) {
          const formattedDate = moment(normalizedBooking.date).format(
            "dddd, MMMM Do YYYY"
          );
          const formattedTime = `${
            normalizedBooking.time.hour
          }:${String(normalizedBooking.time.minute).padStart(2, "0")} ${
            normalizedBooking.time.ampm
          }`;
          setSelectedDate(formattedDate);
          setSelectedTime(formattedTime);
          setSelectedBookingTime(`${normalizedBooking.date} ${formattedTime}`);
        }
        
        // Close any error modals since booking was found successfully
        closeAlertModal();
        
        return true;
      }

      setBooking(null);
      if (!suppressAlerts) {
        showAlertModal(
          "Booking Not Found",
          "Please check your booking code and try again.",
          "error"
        );
      }
      return false;
    } catch (error) {
      console.error("Error fetching booking:", error);
      setBooking(null);
      if (!suppressAlerts) {
        showAlertModal(
          "Error",
          "Error searching for booking. Please try again.",
          "error"
        );
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Normalize booking data structure for both old and new booking flows
  const normalizeBookingData = (bookingData, isNewBookingFlow) => {
    if (isNewBookingFlow) {
      return bookingData.booking;
    } else {
      return bookingData.booking || bookingData;
    }
  };

  const handleBookingCodeSearch = async (overrideCode) => {
    console.log("handleBookingCodeSearch called with:", overrideCode);
    console.log("Current bookingCodeInput:", bookingCodeInput);
    
    // Close any existing error modals before starting a new search
    closeAlertModal();
    
    const searchCode = overrideCode ?? bookingCodeInput;
    // Ensure searchCode is a string before calling trim
    const searchCodeString = String(searchCode || "").trim();
    console.log("Search code string:", searchCodeString);
    
    if (!searchCodeString) {
      console.log("No search code provided");
      showAlertModal(
        "Missing Booking Code",
        "Please enter a booking code to search.",
        "warning"
      );
      return;
    }

    console.log("Starting search for:", searchCodeString);
    setIsSearching(true);
    setLoading(true); // Also set main loading state
    
    try {
      const success = await fetchBookingData(searchCodeString, false);
      console.log("Search result:", success);
      if (success) {
        // Update the booking code input with the found booking code
        setBookingCodeInput(searchCodeString);
      }
      return success;
    } catch (error) {
      console.error("Error in handleBookingCodeSearch:", error);
      showAlertModal(
        "Search Error",
        "An error occurred while searching for your booking. Please try again.",
        "error"
      );
      return false;
    } finally {
      setIsSearching(false);
      // Don't set loading to false here - let fetchBookingData handle it
    }
  };

  const handleBookingTimeUpdate = async (formattedTime) => {
    if (!formattedTime || !booking) {
      showAlertModal(
        "Missing Information",
        "Please select a new date and time.",
        "warning"
      );
      return;
    }

    setIsUpdating(true);
    try {
      const response = await api(
        `booking-flow/booking/${booking.posBookingKey}/update`,
        "PUT",
        {
          selectedBookingTime: formattedTime,
          bookingId: booking._id,
        }
      );

      if (response.data.success) {
        showAlertModal(
          "Success",
          "Booking updated successfully!",
          "success",
          false,
          async () => {
            closeAlertModal();
            // Refresh booking data
            await fetchBookingData();
          }
        );
        // Refresh booking data
        await fetchBookingData();
      } else {
        showAlertModal(
          "Update Failed",
          "Failed to update booking. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error updating booking:", error);
      const status = error.response?.status;
      const data = error.response?.data;
      let errorMessage =
        data?.message ||
        "Error updating booking. Please try again.";
      if (status === 409 && data?.capacityError) {
        errorMessage =
          data.message ||
          "That time slot is full. Please choose another time.";
      }
      showAlertModal(
        status === 409 && data?.capacityError ? "Time unavailable" : "Error",
        errorMessage,
        "error"
      );
    } finally {
      setIsUpdating(false);
      setOpenBookingModal(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!booking) {
      showAlertModal(
        "No Booking Found",
        "No booking found to cancel.",
        "error"
      );
      return;
    }

    try {
      const response = await api(
        `booking-flow/booking/${booking.posBookingKey}/cancel`,
        "PUT",
        {
          bookingId: booking._id,
        }
      );

      if (response.data.success) {
        showAlertModal(
          "Booking Cancelled",
          "Booking cancelled successfully!",
          "success",
          false,
          async () => {
            closeAlertModal();
            // Refresh booking data to show updated status
            await fetchBookingData();
          }
        );
        // Refresh booking data to show updated status
        await fetchBookingData();
      } else {
        showAlertModal(
          "Cancel Failed",
          "Failed to cancel booking. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error cancelling booking:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Error cancelling booking. Please try again.";
      showAlertModal("Error", errorMessage, "error");
    } finally {
      setCheckModalOpen(false);
    }
  };

  const toggleDateModal = (isOpen) => {
    setOpenBookingModal(isOpen);
  };

  const handleTimeSelect = (formattedTime) => {
    setSelectedBookingTime(formattedTime);
    // Parse the formatted time to update selectedDate and selectedTime
    const timeMoment = moment(formattedTime, "YYYY-MM-DD h:mm A");
    setSelectedDate(timeMoment.format("dddd, MMMM Do YYYY"));
    setSelectedTime(timeMoment.format("h:mm A"));
    // Call the update function
    handleBookingTimeUpdate(formattedTime);
  };

  const handleTakeMeThere = () => {
    if (locationData && locationData.googleLinkBooking) {
      window.open(locationData.googleLinkBooking, "_blank");
    } else {
      showAlertModal(
        "Location Link Unavailable",
        "Location link is not available for this booking.",
        "warning"
      );
    }
  };

  if (loading) {
    return (
      <div className="manage-booking-container">
        <div className="manage-booking-content">
          <div className="manage-booking-header">
            <img
              src={ConciergeLogo}
              alt="Concierge Car Wash"
              className="logo"
            />
            <h1 className="manage-title">Manage Your Booking</h1>
            <p className="manage-subtitle">
              View your booking details and make changes if needed.
            </p>
          </div>
          <div className="loading-state">
            <p>Loading booking details...</p>
            {currentBookingId && <p>Booking ID: {currentBookingId}</p>}
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="manage-booking-container">
        <div className="manage-booking-content">
          <div className="manage-booking-header">
            <img
              src={ConciergeLogo}
              alt="Concierge Car Wash"
              className="logo"
            />
            <h1 className="manage-title">Manage Your Booking</h1>
            <p className="manage-subtitle">
              Enter your booking code to view and manage your booking.
            </p>
          </div>

          <div className="booking-code-section">
            <div className="form-group">
              <input
                type="text"
                value={bookingCodeInput}
                onChange={(e) => setBookingCodeInput(e.target.value)}
                placeholder="Enter your booking code"
                className="booking-code-input"
                disabled={isSearching}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    console.log("Enter key pressed, bookingCodeInput:", bookingCodeInput);
                    handleBookingCodeSearch();
                  }
                }}
              />
            </div>
            <button
              type="button"
              className="search-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log("Button clicked, bookingCodeInput:", bookingCodeInput);
                handleBookingCodeSearch();
              }}
              disabled={isSearching || !String(bookingCodeInput || "").trim()}
            >
              {isSearching ? "Searching..." : "Find Booking"}
            </button>
          </div>

          <div className="back-to-home-section">
            <button
              className="back-to-home-button"
              onClick={() => navigate("/")}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-booking-container">
      <div className="manage-booking-content">
        <div className="manage-booking-header">
          <img src={ConciergeLogo} alt="Concierge Car Wash" className="logo" />
          <h1 className="manage-title">Manage Your Booking</h1>
          <p className="manage-subtitle">
            View your booking details and make changes if needed.
          </p>
        </div>

        <div className="booking-details-card">
          {/* Show cancelled status at the top if booking is cancelled */}
          {booking.status === "cancelled" && (
            <div className="booking-status-section">
              <div className="status-badge cancelled">
                <span className="status-icon">❌</span>
                <span className="status-text">Booking Cancelled</span>
              </div>
            </div>
          )}

          <div className="detail-row">
            <span className="detail-label">Booking Number</span>
            <span className="detail-value booking-number">
              {booking.posBookingKey}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Expected Arrival Time</span>
            <span className="detail-value">
              {selectedDate && selectedTime
                ? `${selectedDate} at ${selectedTime}`
                : "Not set"}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Location</span>
            <span className="detail-value">
              {locationData?.seoName ||
                locationData?.name ||
                "Unknown Location"}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Address</span>
            <span className="detail-value">
              {locationData?.address || "Address not available"}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Service</span>
            <span className="detail-value">
              {booking.selectedServices
                ?.map((service) => service.name)
                .join(", ") || "Unknown Service"}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Name</span>
            <span className="detail-value">
              {booking.posResponse?.BookingName ||
                cartData?.step1Data?.email ||
                "Not provided"}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Email</span>
            <span className="detail-value">
              {booking.posResponse?.BookingEmail ||
                cartData?.step1Data?.email ||
                "Not provided"}
            </span>
          </div>
        </div>

        {/* Show take me there button only for active bookings */}
        {booking.status !== "cancelled" && (
          <div className="take-me-there-section">
            <button
              className="take-me-there-button"
              onClick={handleTakeMeThere}
            >
              <span className="icon plane-icon">🚗</span>
              Take me there
            </button>
          </div>
        )}

        {/* Only show changes section if booking is not cancelled */}
        {booking.status !== "cancelled" && (
          <div className="changes-section">
            <h3 className="changes-title">Need to make changes?</h3>
            <div className="change-buttons">
              <button
                className="change-button"
                onClick={() => toggleDateModal(true)}
              >
                <span className="icon calendar-icon">📅</span>
                Change date & time
              </button>
              <button
                className="change-button"
                onClick={() => setCheckModalOpen(true)}
              >
                <span className="icon cancel-icon">❌</span>
                Cancel booking
              </button>
            </div>
          </div>
        )}

        <div className="back-to-home-section">
          <button className="back-to-home-button" onClick={() => navigate("/")}>
            {booking.status === "cancelled" ? "Book Another" : "Back to Home"}
          </button>
        </div>

        <div className="contact-section">
          <p className="contact-text">
            Need help? Contact:{" "}
            <a href="tel:1300279002" className="contact-link">
              1300 279 002
            </a>
          </p>
        </div>
      </div>

      {/* Booking Time Selector */}
      {openBookingModal && (
        <BookingTimeSelector
          isOpen={openBookingModal}
          onClose={() => setOpenBookingModal(false)}
          onTimeSelect={handleTimeSelect}
          location={locationData}
          currentBookingTime={
            selectedBookingTime ||
            `${booking?.date} ${booking?.time?.hour}:${booking?.time?.minute} ${booking?.time?.ampm}`
          }
        />
      )}

      {/* Cancel Confirmation Modal */}
      {checkModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Cancel Booking</h3>
            <p>Are you sure you want to cancel this booking?</p>
            <div className="modal-buttons">
              <button
                onClick={() => setCheckModalOpen(false)}
                className="secondary-button"
              >
                Keep Booking
              </button>
              <button onClick={handleCancelBooking} className="primary-button">
                Cancel Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <Modal
        isOpen={alertModal.isOpen}
        onClose={closeAlertModal}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        showCancel={alertModal.showCancel}
        onConfirm={alertModal.onConfirm || closeAlertModal}
      />
    </div>
  );
};

export default ManageBooking;
